import { useEffect, useMemo, useState } from "react";
import { Send, Trash2 } from "lucide-react";
import { opsApi } from "@/lib/ops";

const NAMES = { hitting: "Hitting", pitching: "Pitching", strength: "Strength", speed: "Speed & Agility" };

export default function ClassAlertsCard({ token }) {
  const api = useMemo(() => opsApi(token), [token]);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/class-alerts").then((d) => setRows(d.alerts)).catch(() => setMsg("Could not load alerts."));
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, ok) => {
    setBusy(true);
    try { setMsg(ok(await fn())); } catch { setMsg("Something went wrong. Please try again."); }
    setBusy(false);
    load();
  };
  const sendNow = () => run(() => api.post("/admin/class-alerts/send", {}), (d) => `${d.sent} alert email(s) sent.`);
  const remove = (id) => run(() => api.del(`/admin/class-alerts/${id}`), () => "Alert removed.");
  return (
    <div data-testid="class-alerts-card" className="mt-10 rounded-2xl border border-white/10 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-white">Class-opening alerts</h2>
          <p className="text-xs font-medium text-white/50">Families who found no class for their athlete's age. Each gets one email when a matching class opens for registration (checked every 15 minutes).</p>
        </div>
        <button data-testid="class-alerts-send-btn" onClick={sendNow} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold text-white border border-white/15 hover:bg-white/10 disabled:opacity-50 rounded-full px-4 py-2.5"><Send size={14} /> {busy ? "Working…" : "Check & send now"}</button>
      </div>
      {msg && <p data-testid="class-alerts-msg" className="mt-3 text-sm font-semibold text-emerald-300">{msg}</p>}
      <div className="mt-4 space-y-2" data-testid="class-alerts-list">
        {!rows.length && <p className="text-sm text-white/40">No alert sign-ups yet.</p>}
        {rows.map((a) => (
          <div key={a.id} data-testid={`class-alert-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/5 px-4 py-3 text-sm">
            <span className="text-white"><b>{a.athlete_name}</b> <span className="text-white/50">· Age {a.age ?? a.dob} · {NAMES[a.category] || a.category} · {a.parent_name} · {a.email}</span></span>
            <span className="flex items-center gap-3">
              <span data-testid={`class-alert-status-${a.id}`} className={`text-xs font-bold uppercase ${a.status === "sent" ? "text-emerald-300" : "text-amber-300"}`}>{a.status === "sent" ? `Sent ${(a.sent_at || "").slice(0, 10)}` : a.status === "sending" ? "Sending" : "Waiting"}</span>
              <button data-testid={`class-alert-delete-${a.id}`} disabled={busy} onClick={() => remove(a.id)} className="text-white/40 hover:text-red-400" aria-label="Remove"><Trash2 size={15} /></button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
